import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useDocumentSubjects } from 'features/knowledgeDocumentViewer/hooks/useDocumentSubjects'
import { useOnSuccessCacheEntriesById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import type {
  AliasHighlight,
  DocumentSubject,
} from 'features/knowledgeDocumentViewer/types/documentSubjects'
import {
  BoundingBoxJsonItem,
  DocumentOcrPage,
  normalizeOcrPages,
} from 'features/knowledgeDocumentViewer/types/ocrViewer'
import {
  type DocumentOcrEntry,
  getCantileverDocumentId,
  getIntraDocSearchDocumentId,
  getSystemDocumentBqId,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { flattenAliasHighlights } from 'features/knowledgeDocumentViewer/utils/aliasHighlights'
import { systemDocumentByDocumentId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { resolvePdfFileRefDual } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexSignedURLs,
} from 'features/rolodex'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  KnowledgeDocumentEntry,
  RolodexEntryType,
  RolodexSignedURLResult,
} from 'types'

interface DocumentTabDataContextType {
  boundingBoxDataState: [DocumentOcrPage, DocumentOcrPage]
  setBoundingBoxDataState: (data: [DocumentOcrPage, DocumentOcrPage]) => void
  documentFilename: string | undefined
  documentSignedURL: RolodexSignedURLResult | undefined
  intraDocSearchDocumentId: string
  sourceDocumentIdPresent: boolean
  documentTableRow: DocumentTableRow | undefined
  documentType: string | undefined
  saveToRolodex: (
    newState: DocumentOcrPage,
    pageIndex?: number
  ) => Promise<void>
  updateBoxLabel: (boxId: string, newLabel: string) => Promise<void>
  knowledgeDocumentEntriesForSelectedDocument: KnowledgeDocumentEntry[]
  isLoadingKnowledgeDocuments: boolean
  /**
   * True once both pipeline-config-scoped lookups for this tab's document id
   * (exact entry id, then the stable `data.document_id` field) have resolved
   * and found nothing — a broken deep link, or a document that is not part of
   * the site's currently selected document set.
   */
  isSelectedDocumentUnavailable: boolean
  selectedDocumentOcrEntries: DocumentOcrEntry[] | undefined
  totalPages: number
  pageNumber: number
  documentSubjects: DocumentSubject[]
  aliasHighlights: AliasHighlight[]
  docReferenceSubjects: DocumentSubject[]
  docReferenceHighlights: AliasHighlight[]
  siteID: string
  documentId: string
}

interface DocumentTabDataProviderProps {
  children: React.ReactNode
  documentId: string
  pageNumber: number
  siteID: string
}

export const DocumentTabDataProvider: React.FC<
  DocumentTabDataProviderProps
> = ({ children, documentId, pageNumber, siteID }) => {
  const { rolodexPatch } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { activeTabId, updateDocumentHistoryItem } =
    useKnowledgeDocumentUXContext()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pipelineConfigCacheScope,
  } = usePipelineConfig()

  const [boundingBoxDataState, setBoundingBoxDataState] = useState<
    [DocumentOcrPage, DocumentOcrPage]
  >([
    { page_number: 1, confirmed: [], unconfirmed: [] },
    { page_number: 2, confirmed: [], unconfirmed: [] },
  ])

  const selectedDocumentOcrLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading
        ? undefined
        : {
            type: [RolodexEntryType.DOCUMENT_OCR],
            tags: getPipelineConfigScopedTags({
              site: siteID,
              'system-document': documentId,
            }),
            limit: 1,
          },
    [
      documentId,
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      siteID,
    ]
  )
  const { data: selectedDocumentOcrEntries } =
    useGetRolodexEntriesByLoadOptions<DocumentOcrEntry[]>(
      selectedDocumentOcrLoadOptions
    )

  const knowledgeDocumentLoadOptions = useMemo(
    () =>
      isPipelineConfigScopeLoading
        ? undefined
        : {
            type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
            tags: getPipelineConfigScopedTags({
              site: siteID,
              'system-document': documentId,
            }),
            limit: documentId ? 1000000 : 0,
          },
    [
      documentId,
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      siteID,
    ]
  )
  const {
    data: knowledgeDocumentEntriesForSelectedDocument,
    isLoading: isLoadingKnowledgeDocuments,
  } = useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
    knowledgeDocumentLoadOptions
  )

  const cacheSelectedDocumentById =
    useOnSuccessCacheEntriesById<SystemDocumentEntry>(pipelineConfigCacheScope)
  const documentLoadOptions = useMemo(
    () => ({
      ids: [documentId],
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: getPipelineConfigScopedTags({ site: siteID }),
    }),
    [documentId, getPipelineConfigScopedTags, siteID]
  )
  const {
    data: selectedDocument,
    isLoading: isLoadingSelectedDocument,
    error: selectedDocumentError,
  } = useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
    documentLoadOptions,
    {
      onSuccess: cacheSelectedDocumentById,
    }
  )

  const exactLookupMissed =
    !!documentId &&
    !isPipelineConfigScopeLoading &&
    !isLoadingSelectedDocument &&
    !selectedDocumentError &&
    selectedDocument !== undefined &&
    !selectedDocument.some((entry) => entry.entry.id === documentId)

  // Durable references (fim subjects/evidence, claims, shared links) carry the
  // stable BQ document_id, not a Rolodex entry id — entry ids are re-minted on
  // every pipeline-config promotion. When the exact-id lookup misses, resolve
  // through the `data.document_id` field and rewrite the tab to the resolved
  // entry id; from then on the tab is indistinguishable from one opened via a
  // list click. Sites whose entries predate the document_id field resolve
  // nothing here and fall through to the unavailable panel.
  const stableIdFallbackLoadOptions = useMemo(
    () =>
      exactLookupMissed
        ? systemDocumentByDocumentId(
            documentId,
            getPipelineConfigScopedTags({ site: siteID })
          )
        : undefined,
    [documentId, exactLookupMissed, getPipelineConfigScopedTags, siteID]
  )
  const { data: stableIdMatches, isLoading: isResolvingStableId } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
      stableIdFallbackLoadOptions
    )
  const resolvedEntryId = exactLookupMissed
    ? stableIdMatches?.[0]?.entry.id
    : undefined

  useEffect(() => {
    if (resolvedEntryId && resolvedEntryId !== documentId) {
      updateDocumentHistoryItem(activeTabId, {
        documentId: resolvedEntryId,
        selectedBoxIds: [],
      })
    }
  }, [activeTabId, documentId, resolvedEntryId, updateDocumentHistoryItem])

  const isSelectedDocumentUnavailable =
    exactLookupMissed &&
    !isResolvingStableId &&
    stableIdMatches !== undefined &&
    resolvedEntryId === undefined

  const selectedSystemDocumentEntry = selectedDocument?.[0]
  const documentFilename = selectedSystemDocumentEntry?.entry.data?.filename
  const sourceDocumentIdPresent = Boolean(
    selectedSystemDocumentEntry?.entry.data.source_document_id
  )
  const intraDocSearchDocumentId = selectedSystemDocumentEntry
    ? getIntraDocSearchDocumentId(selectedSystemDocumentEntry)
    : documentId
  const cantileverDocumentId = selectedSystemDocumentEntry
    ? getCantileverDocumentId(selectedSystemDocumentEntry)
    : undefined
  const signedUrlQuery = useMemo(
    () =>
      resolvePdfFileRefDual(documentId, documentFilename, cantileverDocumentId),
    [cantileverDocumentId, documentFilename, documentId]
  )

  const { results: documentSignedURL } = useRolodexSignedURLs(signedUrlQuery)

  const totalPages = useMemo(() => {
    const pages = selectedDocument?.[0]?.entry.data?.total_pages ?? 0
    return pages === 0 ? 1 : pages
  }, [selectedDocument])

  const extractionDocumentId = selectedSystemDocumentEntry
    ? getSystemDocumentBqId(selectedSystemDocumentEntry)
    : undefined
  const { data: documentSubjectsData } = useDocumentSubjects(
    siteID,
    extractionDocumentId
  )
  const allSubjects = useMemo(
    () => documentSubjectsData?.subjects ?? [],
    [documentSubjectsData]
  )
  const documentSubjects = useMemo(
    () => allSubjects.filter((s) => s.kind !== 'doc_reference'),
    [allSubjects]
  )
  const docReferenceSubjects = useMemo(
    () => allSubjects.filter((s) => s.kind === 'doc_reference'),
    [allSubjects]
  )

  const aliasHighlights = useMemo<AliasHighlight[]>(
    () => flattenAliasHighlights(documentSubjects),
    [documentSubjects]
  )

  const docReferenceHighlights = useMemo<AliasHighlight[]>(
    () => flattenAliasHighlights(docReferenceSubjects),
    [docReferenceSubjects]
  )

  useEffect(() => {
    if (documentId) {
      const pages = normalizeOcrPages(
        selectedDocumentOcrEntries?.[0]?.entry.data?.pages ?? []
      )
      const leftPage = pages.find((p) => p.page_number === pageNumber) ?? {
        page_number: 1,
        confirmed: [],
        unconfirmed: [],
      }
      const rightPage = pages.find((p) => p.page_number === pageNumber + 1) ?? {
        page_number: 2,
        confirmed: [],
        unconfirmed: [],
      }
      setBoundingBoxDataState([leftPage, rightPage])
    }
  }, [documentId, pageNumber, selectedDocumentOcrEntries])

  const saveToRolodex = async (
    newState: DocumentOcrPage,
    pageIndex: number = 0
  ) => {
    const selectedOcrEntry = selectedDocumentOcrEntries?.[0]

    if (!selectedOcrEntry) {
      return
    }

    const newPages = normalizeOcrPages(
      selectedOcrEntry.entry.data?.pages ?? []
    ).map((page) => {
      if (page.page_number === pageNumber + pageIndex) {
        return newState
      }
      return page
    })
    try {
      await rolodexPatch([
        {
          id: selectedOcrEntry.entry.id,
          data: {
            ...selectedOcrEntry.entry.data,
            pages: newPages,
          },
        },
      ])
      successNotification('Saved to Rolodex')
    } catch (error) {
      console.error('Error saving to Rolodex:', error)
      errorNotification('Failed to save to Rolodex')
      throw error
    }
  }

  const updateBoxLabel = async (boxId: string, newLabel: string) => {
    const lastDash = boxId.lastIndexOf('-')
    const boxPageIndex = lastDash !== -1 ? Number(boxId.slice(lastDash + 1)) : 0
    const rawId = lastDash !== -1 ? boxId.slice(0, lastDash) : boxId
    const currentPageData = boundingBoxDataState[boxPageIndex]

    const updateBoxInList = (boxes: BoundingBoxJsonItem[]) =>
      boxes.map((b) => (String(b.id) === rawId ? { ...b, text: newLabel } : b))

    const updatedConfirmed = updateBoxInList(currentPageData.confirmed)
    const updatedUnconfirmed = updateBoxInList(currentPageData.unconfirmed)

    const updatedPageData = {
      ...currentPageData,
      confirmed: updatedConfirmed,
      unconfirmed: updatedUnconfirmed,
    }

    const newState: [DocumentOcrPage, DocumentOcrPage] = [
      boxPageIndex === 0 ? updatedPageData : boundingBoxDataState[0],
      boxPageIndex === 1 ? updatedPageData : boundingBoxDataState[1],
    ]
    setBoundingBoxDataState(newState)
    await saveToRolodex(updatedPageData, boxPageIndex)
  }

  const documentType = selectedDocument?.[0]?.entry.data?.type

  const documentTableRow = useMemo(() => {
    return selectedSystemDocumentEntry
      ? transformSystemDocumentToTableRow(selectedSystemDocumentEntry)
      : undefined
  }, [selectedSystemDocumentEntry])

  const contextValue: DocumentTabDataContextType = {
    boundingBoxDataState,
    setBoundingBoxDataState,
    documentFilename,
    documentSignedURL: documentSignedURL?.[0],
    intraDocSearchDocumentId,
    sourceDocumentIdPresent,
    documentTableRow,
    documentType,
    saveToRolodex,
    updateBoxLabel,
    knowledgeDocumentEntriesForSelectedDocument:
      knowledgeDocumentEntriesForSelectedDocument ?? [],
    isLoadingKnowledgeDocuments,
    isSelectedDocumentUnavailable,
    selectedDocumentOcrEntries,
    totalPages,
    pageNumber,
    documentSubjects,
    aliasHighlights,
    docReferenceSubjects,
    docReferenceHighlights,
    siteID,
    documentId,
  }

  return (
    <DocumentTabDataContext.Provider value={contextValue}>
      {children}
    </DocumentTabDataContext.Provider>
  )
}

const DocumentTabDataContext = createContext<
  DocumentTabDataContextType | undefined
>(undefined)

export const useDocumentTabDataContext = (): DocumentTabDataContextType => {
  const context = useContext(DocumentTabDataContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentTabDataContext must be used within a DocumentTabDataProvider'
    )
  }
  return context
}

/**
 * Returns the document tab data context if inside a DocumentTabDataProvider,
 * or null otherwise. Use this in hooks that may run outside a document tab
 * (e.g. useOcrResults called from useLinkedDocumentsNavigation at the top level).
 */
export const useDocumentTabDataContextOptional =
  (): DocumentTabDataContextType | null => {
    return useContext(DocumentTabDataContext) ?? null
  }
